import React, { useEffect, useState } from "react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Clock, Mail, MapPin, Phone, Send, CheckCircle2, Loader2, ExternalLink, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { formatApiError } from "@/api";
import { mediaUrl, BACKEND_URL } from "@/web/useWebApi";
import { postContact } from "@/web/publicApi";
import { SectionWrap, WButton } from "../theme";

export function FaqSection({ section }) {
  const items = section.data?.items || [];
  return (
    <SectionWrap section={section} narrow>
      <Accordion type="single" collapsible className="divide-y" style={{ borderTop: "1px solid var(--w-border)", borderBottom: "1px solid var(--w-border)" }} data-testid="ws-faq-list">
        {items.map((it, i) => (
          <AccordionItem key={i} value={`q${i}`} className="border-0" style={{ borderColor: "var(--w-border)" }}>
            <AccordionTrigger className="text-left text-base font-bold py-5 hover:no-underline" data-testid={`ws-faq-q-${i}`}>{it.q}</AccordionTrigger>
            <AccordionContent className="text-sm lg:text-base opacity-80 whitespace-pre-line pb-5">{it.a}</AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
      {!items.length && <p className="text-sm opacity-60">Aucune question pour le moment.</p>}
    </SectionWrap>
  );
}

const TODAY = ["Dimanche", "Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi"][new Date().getDay()];

export function HoursSection({ section }) {
  const d = section.data || {};
  return (
    <SectionWrap section={section} muted>
      <div className="grid lg:grid-cols-2 gap-10">
        <div data-testid="ws-hours-table">
          <div className="text-[10px] uppercase tracking-[0.3em] font-bold opacity-50 mb-3 flex items-center gap-2"><Clock className="h-3.5 w-3.5" /> Horaires</div>
          <dl className="divide-y" style={{ borderColor: "var(--w-border)" }}>
            {(d.days || []).map((day) => (
              <div key={day.day} className={`flex items-center justify-between py-2.5 text-sm ${day.day === TODAY ? "font-black" : ""}`} style={{ borderColor: "var(--w-border)" }} data-testid={`ws-hours-${day.day}`}>
                <dt className="flex items-center gap-2">{day.day}{day.day === TODAY && <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5" style={{ background: "var(--w-accent)", color: "var(--w-on-accent)" }}>Aujourd'hui</span>}</dt>
                <dd className="font-mono text-xs">{day.closed ? "Fermé" : `${day.open || "—"} – ${day.close || "—"}`}</dd>
              </div>
            ))}
          </dl>
          {d.note && <p className="mt-4 text-sm opacity-75">{d.note}</p>}
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] font-bold opacity-50 mb-3 flex items-center gap-2"><MapPin className="h-3.5 w-3.5" /> Accès</div>
          {d.address ? <address className="not-italic text-base lg:text-lg font-semibold whitespace-pre-line" data-testid="ws-hours-address">{d.address}</address> : <p className="text-sm opacity-60">Adresse à renseigner.</p>}
          {d.map_url && <WButton as="a" href={d.map_url} target="_blank" rel="noreferrer" variant="outline" size="sm" className="mt-5" data-testid="ws-hours-map">Itinéraire <ExternalLink className="h-3.5 w-3.5" /></WButton>}
        </div>
      </div>
    </SectionWrap>
  );
}

export function NewsSection({ section }) {
  const items = section.data?.items || [];
  return (
    <SectionWrap section={section}>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="ws-news-grid">
        {items.map((n, i) => (
          <article key={i} className="flex flex-col" data-testid={`ws-news-${i}`}>
            {n.image_url && <img src={mediaUrl(n.image_url)} alt="" className="w-full aspect-[16/10] object-cover mb-4" style={{ borderRadius: "var(--w-radius)" }} />}
            {n.date && <time className="text-[10px] uppercase tracking-[0.25em] font-bold" style={{ color: "var(--w-primary)" }}>{new Date(n.date).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}</time>}
            <h3 className="mt-2 text-xl font-black tracking-tight">{n.title}</h3>
            {n.text && <p className="mt-2 text-sm opacity-75 whitespace-pre-line line-clamp-6">{n.text}</p>}
          </article>
        ))}
        {!items.length && <p className="text-sm opacity-60">Aucune actualité publiée.</p>}
      </div>
    </SectionWrap>
  );
}

export function VideoSection({ section }) {
  const d = section.data || {};
  const embed = toEmbed(d.url);
  return (
    <SectionWrap section={section} narrow>
      {embed ? <div className="aspect-video w-full overflow-hidden" style={{ borderRadius: "var(--w-radius)" }}><iframe src={embed} title={section.title || "Vidéo"} className="w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen data-testid="ws-video-frame" /></div>
        : <p className="text-sm opacity-60">Lien vidéo YouTube ou Vimeo à renseigner.</p>}
      {d.caption && <p className="mt-3 text-sm opacity-70 text-center">{d.caption}</p>}
    </SectionWrap>
  );
}

function toEmbed(url = "") {
  const yt = url.match(/(?:youtube\.com\/(?:watch\?v=|shorts\/|embed\/)|youtu\.be\/)([\w-]{6,})/);
  if (yt) return `https://www.youtube-nocookie.com/embed/${yt[1]}`;
  const vm = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vm) return `https://player.vimeo.com/video/${vm[1]}`;
  return "";
}

export function PagOffersSection({ section }) {
  const d = section.data || {};
  const plans = d.plan_items || [];
  return (
    <SectionWrap section={section}>
      {d.intro && <p className="text-base opacity-80 mb-8 max-w-2xl">{d.intro}</p>}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="ws-pag-offers">
        {plans.map((p) => (
          <a key={p.key} href={`${BACKEND_URL}/inscription?bp=${encodeURIComponent(d.bp_code || "")}&plan=${p.key}&cycle=annual`} className="group p-5 flex flex-col border transition-colors hover:border-current" style={{ borderColor: "var(--w-border)", background: "var(--w-card)", borderRadius: "var(--w-radius)" }} data-testid={`ws-plan-${p.key}`}>
            <div className="h-1.5 w-12 mb-4" style={{ background: p.color || "var(--w-primary)" }} />
            <div className="text-lg font-black">{p.name}</div>
            {p.tagline && <div className="text-xs opacity-70 mt-1 flex-1">{p.tagline}</div>}
            <ul className="mt-3 space-y-1 text-[11px] opacity-75">{(p.features || []).slice(0, 3).map((f, i) => <li key={i}>• {typeof f === "string" ? f : f.label || f.text}</li>)}</ul>
            <div className="mt-4 text-xs font-bold uppercase tracking-wider inline-flex items-center gap-1 group-hover:gap-2 transition-all" style={{ color: "var(--w-primary)" }}>Souscrire <ArrowRight className="h-3.5 w-3.5" /></div>
          </a>
        ))}
      </div>
      <p className="mt-6 text-[11px] opacity-60">Chaque souscription réalisée depuis cette page est rattachée au partenaire {d.bp_code}. Prix et service PAG identiques quel que soit le canal.</p>
    </SectionWrap>
  );
}

export function ContactSection({ section, slug, owner, preview }) {
  const d = section.data || {};
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    const h = (e) => setForm((f) => ({ ...f, subject: e.detail?.subject ? `Demande : ${e.detail.subject}` : f.subject }));
    window.addEventListener("wsite:prefill", h);
    try { const pre = sessionStorage.getItem("wsite-prefill"); if (pre) { setForm((f) => ({ ...f, subject: `Demande : ${pre}` })); sessionStorage.removeItem("wsite-prefill"); } } catch { /* ignore */ }
    return () => window.removeEventListener("wsite:prefill", h);
  }, []);
  const submit = async (e) => {
    e.preventDefault();
    if (preview) { toast.info("Aperçu : le formulaire est désactivé"); return; }
    setBusy(true);
    try { await postContact(slug, form); setSent(true); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };
  const email = d.email || owner?.email, phone = d.phone || owner?.phone, address = d.address || owner?.address;
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const input = "w-full h-11 px-3 text-sm bg-transparent border outline-none focus:ring-2 transition-shadow";
  const inputStyle = { borderColor: "var(--w-border)", borderRadius: "var(--w-radius)", "--tw-ring-color": "var(--w-primary)" };
  return (
    <SectionWrap section={section}>
      <div className="grid lg:grid-cols-[1fr_1.3fr] gap-10 lg:gap-16">
        <div className="space-y-6">
          {d.intro && <p className="text-base lg:text-lg opacity-85">{d.intro}</p>}
          <div className="space-y-3 text-sm" data-testid="ws-contact-info">
            {email && <a href={`mailto:${email}`} className="flex items-center gap-3 hover:underline"><Mail className="h-4 w-4" style={{ color: "var(--w-primary)" }} /> {email}</a>}
            {phone && <a href={`tel:${phone}`} className="flex items-center gap-3 hover:underline"><Phone className="h-4 w-4" style={{ color: "var(--w-primary)" }} /> {phone}</a>}
            {address && <div className="flex items-start gap-3"><MapPin className="h-4 w-4 mt-0.5" style={{ color: "var(--w-primary)" }} /> <span className="whitespace-pre-line">{address}</span></div>}
          </div>
        </div>
        {d.form_enabled !== false && (
          sent ? (
            <div className="p-8 flex flex-col items-start gap-3" style={{ background: "var(--w-muted)", borderRadius: "var(--w-radius)" }} data-testid="ws-contact-success">
              <CheckCircle2 className="h-8 w-8" style={{ color: "var(--w-primary)" }} />
              <div className="text-xl font-black">Message envoyé !</div>
              <p className="text-sm opacity-75">Merci {form.name}. Nous vous répondons au plus vite à l'adresse {form.email}.</p>
              <WButton size="sm" variant="outline" onClick={() => { setSent(false); setForm({ name: "", email: "", phone: "", subject: "", message: "" }); }}>Envoyer un autre message</WButton>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-3" data-testid="ws-contact-form">
              <div className="grid sm:grid-cols-2 gap-3">
                <input required minLength={2} value={form.name} onChange={set("name")} placeholder="Votre nom *" className={input} style={inputStyle} data-testid="ws-contact-name" />
                <input required type="email" value={form.email} onChange={set("email")} placeholder="Votre email *" className={input} style={inputStyle} data-testid="ws-contact-email" />
                <input value={form.phone} onChange={set("phone")} placeholder="Téléphone" className={input} style={inputStyle} data-testid="ws-contact-phone" />
                <input value={form.subject} onChange={set("subject")} placeholder="Sujet" className={input} style={inputStyle} data-testid="ws-contact-subject" />
              </div>
              <textarea required minLength={2} rows={5} value={form.message} onChange={set("message")} placeholder="Votre message *" className={`${input} h-auto py-3`} style={inputStyle} data-testid="ws-contact-message" />
              <WButton type="submit" disabled={busy} data-testid="ws-contact-submit">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Envoyer</WButton>
            </form>
          )
        )}
      </div>
    </SectionWrap>
  );
}
